import { useState, useEffect } from 'react';
import { FamSpinner } from '../badges/Badge';
import { useParams } from 'react-router-dom';
import { useUser, SignInButton } from '@clerk/clerk-react';
import { PaymentModal } from '../payment/PaymentModal';
import type { CarGoal } from '../../types';
import {
  Car,
  LogIn,
  Heart,
  TrendingUp,
  Clock,
  Users,
  Share2,
  Copy,
  CheckCheck,
  ChevronRight,
  Gift,
  MapPin,
  Star,
  AlertCircle,
  Home,
} from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────────────────

interface CampaignData {
  id: string;
  title: string;
  description: string;
  goal_amount: number;
  current_amount: number;
  status: string;
  created_at: string;
}

interface VehicleData {
  id: string;
  year: number;
  make: string;
  model: string;
  trim: string | null;
  purchase_price: number;
  image_url: string | null;
  dealer_name: string;
  rank: number;
}

interface PublicCampaignPayload {
  campaign: CampaignData;
  vehicles: VehicleData[];
  creatorFirstName: string;
  supporterCount: number;
}

// ── Days / phase helpers ──────────────────────────────────────────────────────

function daysRemaining(createdAt: string): number {
  const created = new Date(createdAt).getTime();
  const now = Date.now();
  const elapsed = Math.floor((now - created) / (1000 * 60 * 60 * 24));
  return Math.max(0, 7 - elapsed);
}

type Phase = 'friends' | 'investor_network' | 'algorithm' | 'funded';

function getPhase(createdAt: string, status: string): Phase {
  if (status === 'completed') return 'funded';
  const elapsed = Math.floor((Date.now() - new Date(createdAt).getTime()) / (1000 * 60 * 60 * 24));
  if (elapsed >= 7) return 'algorithm';
  if (elapsed >= 3) return 'investor_network';
  return 'friends';
}

function daysUntilNetwork(createdAt: string): number {
  const elapsed = Math.floor((Date.now() - new Date(createdAt).getTime()) / (1000 * 60 * 60 * 24));
  return Math.max(0, 3 - elapsed);
}

const PHASE_CONFIG: Record<Phase, { label: string; color: string; bg: string; border: string; sub: (d: number) => string }> = {
  friends: {
    label: 'Friends & Family',
    color: 'text-rose-400',
    bg: 'bg-rose-500/10',
    border: 'border-rose-500/20',
    sub: (d) => d > 0 ? `Opens to FAM investor network in ${d} ${d === 1 ? 'day' : 'days'}` : 'Opening to FAM network today',
  },
  investor_network: {
    label: 'Open to FAM Network',
    color: 'text-indigo-400',
    bg: 'bg-indigo-500/10',
    border: 'border-indigo-500/20',
    sub: (d) => d > 0 ? `FAM underwrites in ${d} ${d === 1 ? 'day' : 'days'}` : 'Underwriting today',
  },
  algorithm: {
    label: 'Underwriting in Progress',
    color: 'text-amber-400',
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/20',
    sub: () => 'FAM algorithm is generating financing options',
  },
  funded: {
    label: 'Funded',
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/20',
    sub: () => 'This campaign has been fully funded',
  },
};

function PhaseBanner({ createdAt, status }: { createdAt: string; status: string }) {
  const phase = getPhase(createdAt, status);
  const daysLeft = daysRemaining(createdAt);
  const netDays = daysUntilNetwork(createdAt);
  const cfg = PHASE_CONFIG[phase];

  return (
    <div className={`flex items-center gap-3 rounded-2xl border px-4 py-3 ${cfg.bg} ${cfg.border}`}>
      <div className="flex-1 min-w-0">
        <p className={`text-xs font-bold uppercase tracking-widest ${cfg.color}`}>{cfg.label}</p>
        <p className="text-gray-500 text-xs mt-0.5">{cfg.sub(phase === 'friends' ? netDays : daysLeft)}</p>
      </div>
      {phase !== 'funded' && phase !== 'algorithm' && (
        <div className="flex-shrink-0 text-right">
          <p className={`text-xl font-bold tabular-nums ${cfg.color}`}>Day {Math.min(Math.floor((Date.now() - new Date(createdAt).getTime()) / (1000 * 60 * 60 * 24)) + 1, 7)}</p>
          <p className="text-gray-400 text-[10px]">of 7</p>
        </div>
      )}
    </div>
  );
}

// ── VehicleCard ───────────────────────────────────────────────────────────────

function VehicleCard({ vehicle, rank }: { vehicle: VehicleData; rank: number }) {
  return (
    <div className="flex items-center gap-4 bg-white border border-gray-200 rounded-2xl p-4 shadow-sm hover:border-indigo-200 hover:shadow-md transition-all">
      {/* Rank badge */}
      <div
        className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg,#4f46e5,#7c3aed)' }}
      >
        <span className="text-white text-xs font-bold">{rank}</span>
      </div>

      {/* Vehicle image */}
      <div className="flex-shrink-0 w-20 h-14 rounded-xl overflow-hidden bg-gray-100">
        {vehicle.image_url ? (
          <img
            src={vehicle.image_url}
            alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Car className="w-6 h-6 text-gray-400" />
          </div>
        )}
      </div>

      {/* Details */}
      <div className="flex-1 min-w-0">
        <p className="text-gray-900 font-semibold text-sm truncate">
          {vehicle.year} {vehicle.make} {vehicle.model}
          {vehicle.trim ? ` ${vehicle.trim}` : ''}
        </p>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <span className="flex items-center gap-1 text-gray-500 text-xs">
            <MapPin className="w-3 h-3" />
            {vehicle.dealer_name}
          </span>
        </div>
      </div>

      {/* Price */}
      <div className="flex-shrink-0 text-right">
        <p className="text-gray-900 font-bold text-sm">
          ${vehicle.purchase_price.toLocaleString()}
        </p>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export function GoalPage() {
  const { id: campaignId } = useParams<{ id: string }>();
  const { isSignedIn } = useUser();

  const [data, setData] = useState<PublicCampaignPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [paymentType, setPaymentType] = useState<'gift' | 'investment' | null>(null);

  useEffect(() => {
    if (!campaignId) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    let cancelled = false;

    fetch(`/api/campaign-public?id=${encodeURIComponent(campaignId)}`)
      .then(async (res) => {
        if (cancelled) return;
        if (res.status === 404) {
          setNotFound(true);
          return;
        }
        if (!res.ok) {
          setNotFound(true);
          return;
        }
        const json = (await res.json()) as PublicCampaignPayload;
        if (!cancelled) setData(json);
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [campaignId]);

  const shareUrl =
    typeof window !== 'undefined'
      ? window.location.href
      : `https://fam.autos/goal/${campaignId ?? ''}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      const el = document.createElement('textarea');
      el.value = shareUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2500);
  };

  const handleShare = (platform: 'twitter' | 'facebook') => {
    if (!data) return;
    const text = `Help ${data.creatorFirstName} get their ${data.campaign.title}! Support them on FAM.`;
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    };
    window.open(urls[platform], '_blank', 'noopener,noreferrer');
  };

  const navigateToPayment = (type: 'gift' | 'investment') => {
    setPaymentType(type);
  };

  // ── Loading ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <FamSpinner size={64} label="Loading campaign..." className="[&_p]:text-gray-400" />
      </div>
    );
  }

  // ── Not Found ─────────────────────────────────────────────────────────────

  if (notFound || !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-gray-400" />
          </div>
          <h1 className="text-gray-900 text-xl font-bold mb-2">Campaign not found</h1>
          <p className="text-gray-500 text-sm mb-6">
            This campaign may have ended or the link is incorrect.
          </p>
          <a
            href="/"
            className="inline-flex items-center gap-2 text-white px-6 py-3 rounded-2xl font-semibold text-sm"
            style={{ background: 'linear-gradient(135deg,#4f46e5,#7c3aed,#e11d48)' }}
          >
            <Home className="w-4 h-4" />
            Go Home
          </a>
        </div>
      </div>
    );
  }

  const { campaign, vehicles, creatorFirstName, supporterCount } = data;
  const progress = Math.min((campaign.current_amount / campaign.goal_amount) * 100, 100);
  const remaining = campaign.goal_amount - campaign.current_amount;
  const daysLeft = daysRemaining(campaign.created_at);
  const creatorInitial = creatorFirstName.charAt(0).toUpperCase();

  return (
    <div className="min-h-screen bg-gray-50 pb-32 md:pb-0">

      {/* ── 1. Nav ─────────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <a href="/" className="flex items-center gap-2">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: 'linear-gradient(135deg,#4f46e5,#7c3aed,#e11d48)' }}
              >
                <span className="text-white font-extrabold text-sm leading-none">F</span>
              </div>
              <span
                className="font-extrabold text-xl tracking-tight bg-clip-text text-transparent"
                style={{ backgroundImage: 'linear-gradient(135deg,#4f46e5,#7c3aed,#e11d48)' }}
              >
                FAM
              </span>
            </a>
            {isSignedIn ? (
              <a
                href="/"
                className="text-sm text-gray-500 hover:text-gray-900 transition-colors font-medium"
              >
                My Dashboard
              </a>
            ) : (
              <SignInButton mode="modal" fallbackRedirectUrl={`/goal/${campaignId ?? ''}`}>
                <button className="flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors text-sm font-medium">
                  <LogIn className="w-4 h-4" />
                  Sign In
                </button>
              </SignInButton>
            )}
          </div>
        </div>
      </header>

      {/* ── 2. Hero ────────────────────────────────────────────────────────── */}
      <section className="relative">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-indigo-50 via-white to-white pointer-events-none" />

        <div className="container mx-auto px-4 pt-10 pb-8 max-w-2xl relative">
          {/* Creator identity */}
          <div className="flex items-center gap-4 mb-6">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg"
              style={{ background: 'linear-gradient(135deg,#4f46e5,#7c3aed,#e11d48)' }}
            >
              <span className="text-white font-bold text-xl">{creatorInitial}</span>
            </div>
            <div>
              <p className="text-gray-500 text-sm font-medium">
                {creatorFirstName} is raising funds for
              </p>
              <h1 className="text-gray-900 text-2xl md:text-3xl font-bold leading-tight">
                {campaign.title}
              </h1>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mb-2">
            <div className="flex justify-between items-center mb-2">
              <span className="text-gray-500 text-sm">
                <span className="text-gray-900 font-bold text-lg">
                  ${campaign.current_amount.toLocaleString()}
                </span>{' '}
                raised
              </span>
              <span className="text-indigo-600 font-semibold text-sm">
                {progress.toFixed(0)}%
              </span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{
                  width: `${progress}%`,
                  background: 'linear-gradient(90deg,#4f46e5,#7c3aed,#e11d48)',
                }}
              />
            </div>
            <p className="text-gray-400 text-xs mt-2">
              Goal: ${campaign.goal_amount.toLocaleString()} &middot; $
              {remaining.toLocaleString()} remaining
            </p>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-3 mt-5">
            <div className="bg-white border border-gray-200 rounded-2xl py-4 px-3 text-center shadow-sm">
              <p className="text-gray-900 font-bold text-xl">
                ${campaign.current_amount.toLocaleString()}
              </p>
              <p className="text-gray-400 text-xs mt-1">raised</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-2xl py-4 px-3 text-center shadow-sm">
              <div className="flex items-center justify-center gap-1">
                <Clock className="w-4 h-4 text-indigo-500" />
                <p className="text-gray-900 font-bold text-xl">{daysLeft}</p>
              </div>
              <p className="text-gray-400 text-xs mt-1">days left</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-2xl py-4 px-3 text-center shadow-sm">
              <div className="flex items-center justify-center gap-1">
                <Users className="w-4 h-4 text-indigo-400" />
                <p className="text-gray-900 font-bold text-xl">{supporterCount}</p>
              </div>
              <p className="text-gray-400 text-xs mt-1">supporters</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── 3. Phase Banner ────────────────────────────────────────────────── */}
      <section className="container mx-auto px-4 pb-4 max-w-2xl">
        <PhaseBanner createdAt={campaign.created_at} status={campaign.status} />
      </section>

      {/* ── 4. Vehicle Options ─────────────────────────────────────────────── */}
      {vehicles.length > 0 && (
        <section className="container mx-auto px-4 pb-8 max-w-2xl">
          <div className="flex items-center gap-2 mb-4">
            <Car className="w-5 h-5 text-indigo-500" />
            <h2 className="text-gray-900 font-bold text-lg">
              {creatorFirstName}'s vehicle options
            </h2>
          </div>
          <div className="flex flex-col gap-3">
            {vehicles.map((v) => (
              <VehicleCard key={v.id} vehicle={v} rank={v.rank} />
            ))}
          </div>
          <p className="text-gray-400 text-xs mt-3">
            FAM ranks vehicles best-to-worst terms. The final vehicle is selected on Day 7.
          </p>
        </section>
      )}

      {/* ── 4. Campaign Story ──────────────────────────────────────────────── */}
      {campaign.description && (
        <section className="container mx-auto px-4 pb-8 max-w-2xl">
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: 'linear-gradient(135deg,#4f46e5,#7c3aed,#e11d48)' }}
              >
                <span className="text-white font-bold text-sm">{creatorInitial}</span>
              </div>
              <div>
                <p className="text-gray-900 font-semibold text-sm">{creatorFirstName}</p>
                <p className="text-gray-500 text-xs flex items-center gap-1">
                  <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                  FAM Campaign Creator
                </p>
              </div>
            </div>
            <p className="text-gray-700 leading-relaxed text-sm md:text-base whitespace-pre-line">
              {campaign.description}
            </p>
          </div>
        </section>
      )}

      {/* ── 5. Supporters Section ─────────────────────────────────────────── */}
      <section className="container mx-auto px-4 pb-8 max-w-2xl">
        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <Heart className="w-5 h-5 text-rose-500 fill-rose-500" />
            <h3 className="text-gray-900 font-bold">
              {supporterCount === 0
                ? 'Be the first to support'
                : `${supporterCount} ${supporterCount === 1 ? 'person has' : 'people have'} already supported ${creatorFirstName}`}
            </h3>
          </div>
          {supporterCount === 0 ? (
            <p className="text-gray-500 text-sm">
              No contributions yet — be the first to help {creatorFirstName} get their vehicle.
            </p>
          ) : (
            <div className="flex items-center gap-3">
              {/* Avatar stack */}
              <div className="flex -space-x-2">
                {Array.from({ length: Math.min(supporterCount, 5) }).map((_, i) => (
                  <div
                    key={i}
                    className="w-8 h-8 rounded-full border-2 border-white bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center"
                  >
                    <Users className="w-3 h-3 text-white" />
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-sm">
                {supporterCount > 5 ? `+${supporterCount - 5} more` : ''} backing this campaign
              </p>
            </div>
          )}
        </div>
      </section>

      {/* ── 6. Share Section ───────────────────────────────────────────────── */}
      <section className="container mx-auto px-4 pb-10 max-w-2xl">
        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <Share2 className="w-5 h-5 text-indigo-400" />
            <h3 className="text-gray-900 font-bold">
              Help {creatorFirstName} reach their goal
            </h3>
          </div>
          <p className="text-gray-500 text-sm mb-5">
            Share this campaign with people who know {creatorFirstName}.
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => handleShare('twitter')}
              className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2.5 rounded-xl font-medium text-sm transition-colors border border-gray-200"
            >
              Share on X
            </button>
            <button
              onClick={() => handleShare('facebook')}
              className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2.5 rounded-xl font-medium text-sm transition-colors border border-gray-200"
            >
              Share on Facebook
            </button>
            <button
              onClick={handleCopyLink}
              className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2.5 rounded-xl font-medium text-sm transition-colors border border-gray-200"
            >
              {linkCopied ? (
                <>
                  <CheckCheck className="w-4 h-4 text-green-400" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Copy Link
                </>
              )}
            </button>
          </div>
        </div>
      </section>

      {/* ── Footer ─────────────────────────────────────────────────────────── */}
      <footer className="hidden md:block text-center py-8 border-t border-gray-200">
        <p className="text-gray-400 text-sm">
          Powered by{' '}
          <a href="/" className="text-indigo-600 hover:text-indigo-700 transition-colors font-medium">
            FAM
          </a>{' '}
          &middot; fam.autos
        </p>
      </footer>

      {/* ── 4. Sticky CTA Bar ──────────────────────────────────────────────── */}
      <div className="fixed bottom-0 left-0 right-0 z-40 md:relative md:bottom-auto bg-white/95 backdrop-blur-md border-t border-gray-200 shadow-lg md:border-0 md:shadow-none md:bg-transparent">
        <div className="container mx-auto px-4 py-4 max-w-2xl">
          <p className="text-center text-xs text-gray-500 mb-3 md:hidden">
            <Clock className="w-3 h-3 inline mr-1 text-indigo-500" />
            {daysLeft} days left &middot; {supporterCount}{' '}
            {supporterCount === 1 ? 'person' : 'people'} already in
          </p>

          <div className="grid grid-cols-2 gap-3">
            {/* Gift CTA */}
            {isSignedIn ? (
              <button
                onClick={() => navigateToPayment('gift')}
                className="group flex items-center justify-center gap-2 text-white font-bold py-3.5 rounded-2xl shadow-lg transition-all duration-200 active:scale-[0.97] hover:opacity-90"
                style={{ background: 'linear-gradient(135deg,#4f46e5,#7c3aed,#e11d48)' }}
              >
                <Gift className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm md:text-base">Gift {creatorFirstName}</span>
                <ChevronRight className="w-4 h-4 opacity-70 group-hover:translate-x-0.5 transition-transform" />
              </button>
            ) : (
              <SignInButton mode="modal" fallbackRedirectUrl={`/goal/${campaignId ?? ''}`}>
                <button
                  className="group flex items-center justify-center gap-2 text-white font-bold py-3.5 rounded-2xl shadow-lg transition-all duration-200 active:scale-[0.97] hover:opacity-90 w-full"
                  style={{ background: 'linear-gradient(135deg,#4f46e5,#7c3aed,#e11d48)' }}
                >
                  <Gift className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm md:text-base">Gift {creatorFirstName}</span>
                  <ChevronRight className="w-4 h-4 opacity-70 group-hover:translate-x-0.5 transition-transform" />
                </button>
              </SignInButton>
            )}

            {/* Invest CTA */}
            {isSignedIn ? (
              <button
                onClick={() => navigateToPayment('investment')}
                className="group flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold py-3.5 rounded-2xl shadow-lg shadow-indigo-900/50 transition-all duration-200 active:scale-[0.97]"
              >
                <TrendingUp className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm md:text-base">Invest in {creatorFirstName}</span>
                <ChevronRight className="w-4 h-4 opacity-70 group-hover:translate-x-0.5 transition-transform" />
              </button>
            ) : (
              <SignInButton mode="modal" fallbackRedirectUrl={`/goal/${campaignId ?? ''}`}>
                <button className="group flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold py-3.5 rounded-2xl shadow-lg shadow-indigo-900/50 transition-all duration-200 active:scale-[0.97] w-full">
                  <TrendingUp className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm md:text-base">Invest in {creatorFirstName}</span>
                  <ChevronRight className="w-4 h-4 opacity-70 group-hover:translate-x-0.5 transition-transform" />
                </button>
              </SignInButton>
            )}
          </div>
        </div>
      </div>

      {/* ── Payment Modal ───────────────────────────────────────────────────── */}
      {paymentType && data && (
        <PaymentModal
          isOpen={!!paymentType}
          onClose={() => setPaymentType(null)}
          goal={{
            id: campaign.id,
            user_id: '',
            title: campaign.title,
            description: campaign.description,
            goal_amount: campaign.goal_amount,
            current_amount: campaign.current_amount,
            image_url: '',
            status: campaign.status as CarGoal['status'],
            created_at: new Date(campaign.created_at),
            updated_at: new Date(campaign.created_at),
          }}
          onSuccess={() => setPaymentType(null)}
        />
      )}
    </div>
  );
}
